import { useEffect, useState } from "react";
import { Loader2 } from "lucide-react";
import { toast } from "sonner";
import { http, formatApiError } from "@/lib/api";
import { useProfile } from "@/context/ProfileContext";
import { FEELINGS, speak } from "@/lib/buddy";
import { Dialog, DialogContent, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { BuddyAvatar } from "@/components/buddy/BuddyAvatar";
import { FeelingIcon } from "@/components/buddy/FeelingIcon";

const greeting = () => { const h = new Date().getHours(); return h < 12 ? "Morning check-in" : h < 17 ? "Afternoon check-in" : "Evening check-in"; };

const dismissKey = (date) => `cl_checkin_dismissed_${date}`;

const FeelingStep = ({ buddy, name, onPick }) => (
  <>
    <p className="text-stone-600 mt-1">Good to see you, {name}! How are you feeling today?</p>
    <div className="grid grid-cols-4 gap-2 mt-5">
      {FEELINGS.map(([k, label, color]) => (
        <button key={k} onClick={() => onPick(k)} data-testid={`checkin-feeling-${k}`}
          className="rounded-2xl p-3 flex flex-col items-center gap-1.5 text-xs font-medium text-[#2C4A3E] hover:-translate-y-0.5 transition-transform" style={{ background: color }}>
          <FeelingIcon feeling={k} className="w-6 h-6" />{label}
        </button>
      ))}
    </div>
  </>
);

const GoalStep = ({ suggestions, goal, setGoal, onSave, busy }) => (
  <>
    <p className="text-stone-600 mt-1">Let's pick one small goal for today.</p>
    <div className="flex flex-col gap-2 mt-4">
      {suggestions.map((g, i) => (
        <button key={i} onClick={() => setGoal(g)} data-testid={`checkin-goal-suggestion-${i}`}
          className={`text-left text-sm px-4 py-2.5 rounded-xl border transition-colors ${goal === g ? "bg-[#2C4A3E] text-white border-[#2C4A3E]" : "bg-white border-[#E6DEC3] hover:border-[#2C4A3E]"}`}>{g}</button>
      ))}
    </div>
    <input value={goal} onChange={(e) => setGoal(e.target.value)} maxLength={160} placeholder="Or type your own goal…" data-testid="checkin-goal-input"
      className="w-full mt-3 px-4 py-2.5 rounded-xl bg-white border border-[#E6DEC3] focus:outline-none focus:border-[#2C4A3E] text-sm" />
    <button onClick={onSave} disabled={busy || goal.trim().length < 2} data-testid="checkin-save-btn"
      className="mt-5 w-full inline-flex justify-center items-center gap-2 px-6 py-3 rounded-full bg-[#2C4A3E] text-white disabled:opacity-50">
      {busy && <Loader2 className="w-4 h-4 animate-spin" />} Start my day
    </button>
  </>
);

export const DailyCheckIn = () => {
  const { profile, buddy, checkinOpen, setCheckinOpen, setTodayCheckin } = useProfile();
  const [data, setData] = useState(null);
  const [feeling, setFeeling] = useState(null);
  const [goal, setGoal] = useState("");
  const [busy, setBusy] = useState(false);
  const [done, setDone] = useState(null);

  useEffect(() => {
    if (!buddy) return;
    http.get("/buddy/checkin/today").then((r) => {
      setData(r.data); setTodayCheckin(r.data.checkin);
      if (!r.data.checkin && !sessionStorage.getItem(dismissKey(r.data.date))) setCheckinOpen(true);
    }).catch(() => {});
  }, [buddy?.id]); // eslint-disable-line react-hooks/exhaustive-deps

  const close = (open) => { if (!open && data) sessionStorage.setItem(dismissKey(data.date), "1"); setCheckinOpen(open); if (!open) { setDone(null); setFeeling(null); } };
  const save = async () => {
    setBusy(true);
    try {
      const r = await http.post("/buddy/checkin", { feeling, goal });
      setDone(r.data.checkin); setTodayCheckin(r.data.checkin);
      speak(r.data.checkin.buddy_line, buddy.voice).catch(() => {});
    } catch (e) { toast.error(formatApiError(e)); }
    setBusy(false);
  };
  if (!buddy || !data) return null;

  return (
    <Dialog open={checkinOpen} onOpenChange={close}>
      <DialogContent className="bg-[#FDFBF7] rounded-3xl max-w-md" data-testid="checkin-dialog">
        <div className="flex items-center gap-4">
          <BuddyAvatar buddy={buddy} className="w-16 h-16" testId="checkin-avatar" />
          <div><DialogTitle className="font-display text-2xl text-[#2C4A3E]">{greeting()}</DialogTitle>
            <DialogDescription className="text-xs text-stone-500">with {buddy.name}</DialogDescription></div>
        </div>
        {done ? (
          <div data-testid="checkin-done">
            <p className="font-display text-lg text-[#2C4A3E] mt-2">{done.buddy_line}</p>
            <p className="text-sm text-stone-600 mt-3">Today's goal: <b>{done.goal}</b></p>
            <button onClick={() => close(false)} data-testid="checkin-close-btn" className="mt-5 w-full px-6 py-3 rounded-full bg-[#2C4A3E] text-white">Let's go!</button>
          </div>
        ) : !feeling ? <FeelingStep buddy={buddy} name={profile.name} onPick={(f) => { setFeeling(f); setGoal(""); }} />
          : <GoalStep suggestions={data.suggestions[feeling] || []} goal={goal} setGoal={setGoal} onSave={save} busy={busy} />}
      </DialogContent>
    </Dialog>
  );
};
